import { filter, first, groupBy, last, map, round, split, startCase, values } from 'lodash';
import pluralize from 'pluralize';
import { useState } from 'react';

import { Image } from '@/components/image';
import { Locked } from '@/components/locked';
import { Text } from '@/components/text';
import { Chart } from '@/components/ui/chart';
import { generateChartConfig } from '@/components/ui/chart/utils';
import { Select } from '@/components/ui/inputs/select';
import { CONGESTION_ATTRIBUTES } from '@/lib/constants';
import { CongestionEntryApiInterface, CongestionTimelineApiInterface } from '@/types/api-types';
import { LineAndAreaSettings } from '@/types/charts';
import { getISOStringFromWeek, tryParseDate } from '@/utils/format/date-time';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface CongestionChartProps {
  id: string;
  data: CongestionTimelineApiInterface;
  chartColors?: string[];
  radioTabsClassName?: string;
  selectBoxVariant?: 'small';
  attributesToInclude?: string[];
  chartWrapperClassName?: string;
  isLocked?: boolean;
  className?: string;
  showLatestDaysTotal?: boolean;
  chartClassName?: string;
  chartSettings?: LineAndAreaSettings;
}

const CongestionChart = ({
  id,
  data,
  chartColors,
  radioTabsClassName,
  selectBoxVariant,
  attributesToInclude = [...values(CONGESTION_ATTRIBUTES)],
  chartWrapperClassName,
  showLatestDaysTotal,
  isLocked,
  className,
  chartClassName,
  chartSettings,
}: CongestionChartProps) => {
  const { categories } = data;

  const groupedCategories = groupBy(categories, 'mainCategory');

  // Get the first sub-category to use as the selected option when the page loads
  const firstSubCategory = first(first(values(groupedCategories)));
  const firstOption = `${firstSubCategory?.mainCategory}-${firstSubCategory?.subCategory}`;

  const [selectedOption, setSelectedOption] = useState(firstOption);

  // Split the main and sub categories out so we can use them to filter the data
  const mainCategory = split(selectedOption, '-')[0];
  const subCategory = split(selectedOption, '-')[1];

  const filteredData = filter(groupedCategories[mainCategory], { subCategory });

  const lastEntryDaysTotal = filteredData.length ? round(last(filteredData[0].entries)?.daysTotal || 0, 1) : 1;

  const handleCategoryChange = (value: string) => {
    if (!value) return;

    setSelectedOption(value);

    // Analytics Event
    trackUser.event('Changed Congestion Chart Tab', { tab: value });
  };

  const { base } = styles();

  const { chartWrapper, latestDaysTotal, latestTotalDaysHeading, latestTotalDaysValue, radioTabs, chart } = styles();

  const transformedData = filteredData.flatMap((category) =>
    category.entries.map((entry) => ({
      date: tryParseDate(`${getISOStringFromWeek(entry.timestamp)}T00:00:00`),
      ...attributesToInclude.reduce(
        (acc, attr) => ({
          ...acc,
          [attr]: entry[attr as keyof CongestionEntryApiInterface],
        }),
        {},
      ),
    })),
  );

  // Generate chart config for the attributes
  const chartConfig = generateChartConfig({
    keys: attributesToInclude,
    colorOverrides: chartColors,
  });

  if (isLocked) {
    return (
      <div className={base({ className })}>
        <Locked id="Chart:Congestion" isLocked={isLocked} isBlurred isCentered iconSize="lg">
          <Image src="/assets/pricing-chart.png" alt="Pricing chart example" />
        </Locked>
      </div>
    );
  }

  return (
    <div className={base({ className })}>
      <div className="flex flex-col lg:flex-row">
        {map(groupedCategories, (item, key) => {
          const chartCategories = map(item, (option) => {
            return {
              label: startCase(option.subCategory),
              // Add the main and sub categories to the value
              // so we can use it to filter the data
              value: `${key}-${option.subCategory}`,
            };
          });

          return (
            <div className={radioTabs({ className: radioTabsClassName })} key={key}>
              <Text variant="label" className="mb-2">
                {startCase(key)}
              </Text>

              <Select
                id={`options-${id}`}
                options={[
                  {
                    items: chartCategories,
                  },
                ]}
                onSelect={(selection) => handleCategoryChange(selection?.value)}
                selected={selectedOption}
                placeholder="Select a category"
                size={selectBoxVariant}
                className="lg:w-56"
              />
            </div>
          );
        })}
        {showLatestDaysTotal && (
          <div className={latestDaysTotal()}>
            <div className={latestTotalDaysHeading()}>Latest</div>
            <p className={latestTotalDaysValue()}>
              {lastEntryDaysTotal} {pluralize('day', lastEntryDaysTotal)}
            </p>
          </div>
        )}
      </div>

      <div className={chartWrapper({ className: chartWrapperClassName })}>
        <Chart
          type="area"
          data={transformedData}
          chartConfig={chartConfig}
          className={chart({ className: chartClassName })}
          settings={chartSettings}
        />
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative rounded-md bg-white',
    latestDaysTotal: 'ml-auto mt-2 min-w-20',
    latestTotalDaysHeading: 'mb-0 text-right text-baseSm font-normal text-grey-400',
    latestTotalDaysValue: 'mb-0 -translate-y-0.5 text-right text-baseLg font-medium leading-none',
    radioTabs: 'mb-4 mr-2 lg:mb-12',
    chartWrapper: null,
    chart: 'h-[300px]',
  },
});

export { CongestionChart };
